import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_LIGHT_URL, COMPANY } from "@/lib/brand";

const NAV = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/products", label: "Products", testid: "nav-products-link" },
  { to: "/about", label: "About Us", testid: "nav-about-link" },
  { to: "/career", label: "Career", testid: "nav-career-link" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#E0E5E2]"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
        >
          <span className="relative inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 shrink-0">
            <img
              src={LOGO_LIGHT_URL}
              alt="Lenax Pharmaceuticals logo"
              className="w-full h-full object-contain"
            />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-serif text-xl md:text-2xl text-[#1E8A2E] tracking-tight">
              Lenax
            </span>
            <span className="overline text-[10px] text-[#7A827D] mt-1">
              Pharmaceuticals
            </span>
          </span>
        </Link>

        <nav
          data-testid="primary-nav"
          className="hidden md:flex items-center gap-10"
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors ${
                  isActive
                    ? "text-[#121413]"
                    : "text-[#4A524D] hover:text-[#121413]"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative inline-block py-1">
                  {item.label}
                  <span
                    className={`absolute left-0 -bottom-0.5 h-[2px] bg-[#D92D2D] transition-all duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center">
          <a
            href={`mailto:${COMPANY.info_email}`}
            data-testid="header-contact-cta"
            className="inline-flex items-center px-5 py-2.5 rounded-sm bg-[#121413] text-white text-sm font-medium hover:bg-[#D92D2D] transition-colors duration-300"
          >
            Contact Us
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-sm border border-[#E0E5E2] text-[#121413]"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-[#E0E5E2] bg-white"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`${item.testid}-mobile`}
                className={({ isActive }) =>
                  `text-base font-medium ${
                    isActive ? "text-[#D92D2D]" : "text-[#121413]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={`mailto:${COMPANY.info_email}`}
              data-testid="mobile-contact-cta"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-sm bg-[#121413] text-white text-sm font-medium"
            >
              Contact Us
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
